package com.evoapps.evoshift.ui.onboarding

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CalendarMonth
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.WorkplaceFinancials
import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.ui.billing.label
import com.evoapps.evoshift.ui.finance.formatDecimal
import com.evoapps.evoshift.ui.workplaces.SimpleTextInputDialog
import com.evoapps.evoshift.ui.workplaces.marketName
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.TestTags
import com.evoapps.evoshift.ui.util.dayShort
import com.evoapps.evoshift.ui.workplaces.AvailabilitySlotsDialog
import com.evoapps.evoshift.ui.workplaces.CreateShiftTemplateDialog
import com.evoapps.evoshift.viewmodels.OnboardingUiState
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.StandoutCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.onboarding_hours_days
import evoshift.composeapp.generated.resources.onboarding_hours_invalid
import evoshift.composeapp.generated.resources.onboarding_hours_preview
import evoshift.composeapp.generated.resources.onboarding_review_defaults_desc
import evoshift.composeapp.generated.resources.onboarding_review_defaults_title
import evoshift.composeapp.generated.resources.onboarding_review_edit
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.onboarding_market
import evoshift.composeapp.generated.resources.onboarding_review_no_sales_plan
import evoshift.composeapp.generated.resources.onboarding_review_settings_hint
import evoshift.composeapp.generated.resources.onboarding_review_workplace_title
import evoshift.composeapp.generated.resources.onboarding_step_finance
import evoshift.composeapp.generated.resources.onboarding_summary_plan
import evoshift.composeapp.generated.resources.settings_name
import evoshift.composeapp.generated.resources.settings_workplace_name
import evoshift.composeapp.generated.resources.settings_availability_slots
import evoshift.composeapp.generated.resources.settings_no_slots
import kotlinx.datetime.DayOfWeek
import org.jetbrains.compose.resources.stringResource

private val Muted = Color.White.copy(alpha = 0.78f)

/**
 * The owner's last step lists what onboarding set up, each with a pencil that edits it right
 * there (SH-62): the starter shifts (the skeleton editor's shift dialog), the open days of the
 * weekly skeleton (the day chips) and the availability slots (the settings' slots dialog).
 */
@Composable
internal fun OnboardingDefaultsReview(state: OnboardingUiState, actions: OnboardingActions) {
    val workplace = state.workplace ?: return
    var editingShift by remember { mutableStateOf<ShiftTemplate?>(null) }
    var editingOpenDays by remember { mutableStateOf(false) }
    var editingSlots by remember { mutableStateOf(false) }

    StandoutCard(Modifier.fillMaxWidth().testTag(TestTags.ONBOARDING_REVIEW_DEFAULTS)) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                stringResource(Res.string.onboarding_review_defaults_title),
                color = LightGold,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
            Text(stringResource(Res.string.onboarding_review_defaults_desc), color = Muted, style = MaterialTheme.typography.bodySmall)

            if (state.starterShifts.isNotEmpty()) {
                ReviewSection(stringResource(Res.string.onboarding_hours_preview))
                state.starterShifts.forEachIndexed { index, shift ->
                    ReviewRow(shift.name, "${shift.startTime} – ${shift.endTime}", TestTags.onboardingEditShift(index)) { editingShift = shift }
                }
                val days = DayOfWeek.entries.filterIndexed { index, _ -> index + 1 in state.starterOpenDays }.map { dayShort(it) }
                ReviewRow(stringResource(Res.string.onboarding_hours_days), days.joinToString(", "), TestTags.ONBOARDING_EDIT_OPEN_DAYS) {
                    editingOpenDays = true
                }
            }

            ReviewSection(stringResource(Res.string.settings_availability_slots))
            val slots = workplace.settings.definedAvailabilitySlots
            if (slots.isEmpty()) {
                ReviewRow(stringResource(Res.string.settings_no_slots), null, TestTags.onboardingEditSlot(0)) { editingSlots = true }
            }
            slots.forEachIndexed { index, slot ->
                ReviewRow(slot.label, "${slot.startTime} – ${slot.endTime}", TestTags.onboardingEditSlot(index)) { editingSlots = true }
            }
        }
    }

    editingShift?.let { shift ->
        CreateShiftTemplateDialog(
            initialName = shift.name,
            initialStart = shift.startTime,
            initialEnd = shift.endTime,
            initialRole = shift.role.orEmpty(),
            initialSkills = shift.skills,
            availableRoles = workplace.settings.availableRoles,
            availableSkills = workplace.settings.workplaceSkills,
            onDismiss = { editingShift = null },
            onConfirm = { name, start, end, role, skills ->
                actions.onUpdateStarterShift(shift.copy(name = name, startTime = start, endTime = end, role = role.ifBlank { null }, skills = skills))
                editingShift = null
            }
        )
    }
    if (editingOpenDays) {
        OpenDaysDialog(
            initial = state.starterOpenDays,
            onDismiss = { editingOpenDays = false },
            onSave = {
                actions.onUpdateOpenDays(it)
                editingOpenDays = false
            }
        )
    }
    if (editingSlots) {
        AvailabilitySlotsDialog(
            slots = workplace.settings.definedAvailabilitySlots,
            onDismiss = { editingSlots = false },
            onRemove = { actions.onRemoveAvailabilitySlot(it.id) },
            onAdd = actions.onAddAvailabilitySlot
        )
    }
}

/**
 * The owner's last step shows the workplace onboarding set up, to check before the dashboard
 * (SH-63): its name (renamed here), market, currency and time zone, the sales plan (the settings'
 * dialog) and the plan (the trial; SH-222). Everything else (roles, payroll, swap policy) stays in the workplace
 * settings, which the card points to.
 */
@Composable
internal fun OnboardingWorkplaceReview(state: OnboardingUiState, actions: OnboardingActions) {
    val workplace = state.workplace ?: return
    val settings = workplace.settings
    var renaming by remember { mutableStateOf(false) }

    StandoutCard(Modifier.fillMaxWidth().testTag(TestTags.ONBOARDING_REVIEW_WORKPLACE)) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                stringResource(Res.string.onboarding_review_workplace_title),
                color = LightGold,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
            ReviewRow(stringResource(Res.string.settings_name), workplace.name, TestTags.ONBOARDING_EDIT_NAME) { renaming = true }
            ReviewRow(
                stringResource(Res.string.onboarding_market),
                "${settings.market.flag} ${marketName(settings.market)} · ${settings.currency} · ${settings.timezone}",
                tag = null,
                onEdit = null
            )
            ReviewRow(stringResource(Res.string.onboarding_step_finance), salesPlanText(state.salesTargets), TestTags.ONBOARDING_EDIT_SALES_TARGETS, actions.onEditSalesTargets)
            ReviewRow(
                stringResource(Res.string.onboarding_summary_plan, PlanTier.TRIAL.label()),
                null,
                tag = null,
                onEdit = null
            )
            Text(stringResource(Res.string.onboarding_review_settings_hint), color = Muted, style = MaterialTheme.typography.bodySmall)
        }
    }

    if (renaming) {
        SimpleTextInputDialog(
            title = stringResource(Res.string.settings_workplace_name),
            label = stringResource(Res.string.settings_workplace_name),
            initialValue = workplace.name,
            onDismiss = { renaming = false },
            onConfirm = {
                actions.onRenameWorkplace(it)
                renaming = false
            }
        )
    }
}

/** "Mon 4,000 · Fri 9,000", or that there is no plan yet. */
@Composable
private fun salesPlanText(targets: Map<String, Double>): String {
    val days = DayOfWeek.entries.mapNotNull { day ->
        targets[WorkplaceFinancials.salesTargetKey(day)]?.takeIf { it > 0.0 }?.let { "${dayShort(day)} ${formatDecimal(it, 0)}" }
    }
    return if (days.isEmpty()) stringResource(Res.string.onboarding_review_no_sales_plan) else days.joinToString(" · ")
}

/** A heading inside a review card. */
@Composable
internal fun ReviewSection(title: String) {
    HorizontalDivider(Modifier.padding(top = 4.dp), color = Color.White.copy(alpha = 0.12f))
    Text(title.uppercase(), color = LightGold.copy(alpha = 0.85f), style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
}

/** One reviewed item: its name, its value, and a pencil that edits it ([tag] for tests). */
@Composable
internal fun ReviewRow(title: String, value: String?, tag: String?, onEdit: (() -> Unit)?) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f)) {
            Text(title, color = Color.White, fontWeight = FontWeight.SemiBold)
            if (!value.isNullOrBlank()) Text(value, color = Muted, style = MaterialTheme.typography.bodySmall)
        }
        if (onEdit != null) {
            IconButtonWithTooltip(onClick = onEdit, modifier = Modifier.handCursor().then(if (tag != null) Modifier.testTag(tag) else Modifier), tooltip = stringResource(Res.string.onboarding_review_edit, title)) {
                Icon(
                    Icons.Default.Edit,
                    contentDescription = stringResource(Res.string.onboarding_review_edit, title),
                    tint = LightGold,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
}

/** Which days the starter day runs on (the weekly skeleton); at least one. */
@Composable
internal fun OpenDaysDialog(initial: Set<Int>, onDismiss: () -> Unit, onSave: (Set<Int>) -> Unit) {
    var days by remember { mutableStateOf(initial) }
    EvoDialog(
        title = stringResource(Res.string.onboarding_hours_days),
        icon = Icons.Default.CalendarMonth,
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.save), { onSave(days) }, enabled = days.isNotEmpty())
    ) {
        OpenDayChips(days) { iso -> days = if (iso in days) days - iso else days + iso }
        if (days.isEmpty()) Text(stringResource(Res.string.onboarding_hours_invalid), color = Color(0xFFFFB4AB))
    }
}
